// Blog posts are authored as ES modules exporting { meta, body }.
// `body` is a markdown string rendered by react-markdown on the article page.
// Add a new post by dropping a file here and importing it below.

import sbaVsRevenue from "./sba-vs-revenue-financing";
import prepareApplication from "./prepare-business-loan-application-2026";
import redFlagsBroker from "./5-red-flags-loan-broker";
import fintechVsBank from "./fintech-vs-traditional-bank-lenders";
import fintechFaster from "./why-fintech-approves-faster";
import businessCreditVsPersonal from "./business-credit-vs-personal-credit";
import workingCapitalVsLoc from "./working-capital-vs-line-of-credit";
import trueCostMca from "./true-cost-of-merchant-cash-advance";
import sba7aVs504 from "./sba-7a-vs-sba-504";
import timeInBusiness from "./time-in-business-funding-options";
import whatIsFintech from "./what-is-a-fintech-lender";
import fintechVsLocalBank from "./fintech-vs-local-bank-day-to-day";
// January 2026 batch
import eightTypes from "./8-types-of-business-financing";
import badCreditLoans from "./bad-credit-business-loans-2026";
import ratesHurtCredit from "./does-checking-your-rate-hurt-your-credit";
import equipmentVsLeasing from "./equipment-financing-vs-leasing";
import mcaFactorRates from "./how-mca-factor-rates-work";
import mcaVsLoc from "./mca-vs-line-of-credit";
import afterPrequalify from "./what-happens-after-you-prequalify";
import combiningWcLoc from "./combining-working-capital-and-line-of-credit";
// June 2026 — first AI Writer pillar
import locCompleteGuide from "./business-line-of-credit-complete-guide";

const posts = [
  locCompleteGuide,
  eightTypes,
  badCreditLoans,
  ratesHurtCredit,
  equipmentVsLeasing,
  mcaFactorRates,
  mcaVsLoc,
  afterPrequalify,
  combiningWcLoc,
  trueCostMca,
  fintechVsLocalBank,
  whatIsFintech,
  timeInBusiness,
  sba7aVs504,
  workingCapitalVsLoc,
  sbaVsRevenue,
  fintechVsBank,
  fintechFaster,
  prepareApplication,
  redFlagsBroker,
  businessCreditVsPersonal,
];

export const getAllPosts = () =>
  posts
    .map((p) => p.meta)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

export const getPostBySlug = (slug) =>
  posts.find((p) => p.meta.slug === slug) || null;

export const catSlug = (cat) =>
  (cat || "")
    .toLowerCase()
    .replace(/['']/g, "")           // drop apostrophes so "Buyer's" → "buyers"
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export const getPostsByCategorySlug = (slug) =>
  getAllPosts().filter((p) => catSlug(p.category) === slug);

export const getCategories = () => {
  const map = new Map();
  posts.forEach((p) => {
    const cat = p.meta.category;
    if (!map.has(cat)) map.set(cat, { name: cat, slug: catSlug(cat), count: 0 });
    map.get(cat).count += 1;
  });
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
};

// SEO copy per category — used by the category landing pages so each one has
// distinct title/description and can rank on its own long-tail keyword set.
// heroImage: the signature cover photo for each category landing page.
export const CATEGORY_SEO = {
  products: {
    heading: "Product breakdowns",
    tagline:
      "Every business funding product, decoded — SBA, MCA, equipment, working capital, and beyond. Read this before you compare offers.",
    seoTitle: "Business Funding Products, Explained — Steady Path Journal",
    seoDescription:
      "Plain-English breakdowns of every business funding product: SBA loans, merchant cash advances, working capital, equipment financing, and lines of credit.",
    heroImage:
      "https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&w=1600",
  },
  sba: {
    heading: "SBA loans",
    tagline:
      "The government's small business loan program, mapped out step by step: 7(a), 504, prequalification, and the paperwork that decides whether you close.",
    seoTitle: "SBA Loans — Field Notes from a Working Broker",
    seoDescription:
      "Everything about SBA 7(a) and 504 loans — how to qualify, what documents matter, timeline, common denial reasons, and how brokers structure the file.",
    heroImage:
      "https://images.pexels.com/photos/33866469/pexels-photo-33866469.jpeg?auto=compress&cs=tinysrgb&w=1600",
  },
  credit: {
    heading: "Credit strategy",
    tagline:
      "Personal credit, business credit, and how lenders actually read the file. Rebuild, protect, and separate — before you apply.",
    seoTitle: "Business Credit Strategy — Steady Path Journal",
    seoDescription:
      "Practical guides on business credit vs. personal credit, credit repair for business loans, and how underwriters actually read your credit file.",
    heroImage:
      "https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg?auto=compress&cs=tinysrgb&w=1600",
  },
  strategy: {
    heading: "Funding strategy",
    tagline:
      "When to stack products, when to wait, and when to walk. Judgment calls we make on the phone with clients every day.",
    seoTitle: "Business Funding Strategy — Steady Path Journal",
    seoDescription:
      "Strategy-first guides for business owners: when to combine funding products, when to hold off, and how to think about capital as a founder.",
    heroImage:
      "https://images.pexels.com/photos/590041/pexels-photo-590041.jpeg?auto=compress&cs=tinysrgb&w=1600",
  },
  playbook: {
    heading: "The broker's playbook",
    tagline:
      "Insider tactics we actually use to move applications through underwriting — the emails, the file structure, the phone calls.",
    seoTitle: "Business Loan Broker Playbook — Steady Path Journal",
    seoDescription:
      "The exact playbook a working commercial finance broker uses to prepare an application, structure a file, and negotiate with lenders.",
    heroImage:
      "https://images.pexels.com/photos/6963622/pexels-photo-6963622.jpeg?auto=compress&cs=tinysrgb&w=1600",
  },
  "lender-basics": {
    heading: "Lender basics",
    tagline:
      "How banks, fintech lenders, and non-bank finance companies actually work — and why the same application gets different answers from each.",
    seoTitle: "Fintech vs. Bank Lenders — Steady Path Journal",
    seoDescription:
      "A working broker's take on fintech lenders vs. traditional banks: speed, cost, documentation, and which businesses fit which lender type.",
    heroImage:
      "https://images.pexels.com/photos/3760263/pexels-photo-3760263.jpeg?auto=compress&cs=tinysrgb&w=1600",
  },
  "buyers-guide": {
    heading: "Buyer's guide",
    tagline:
      "How to read an offer, decode the fee schedule, and avoid the two contract clauses that ruin most deals.",
    seoTitle: "Business Loan Buyer's Guide — Steady Path Journal",
    seoDescription:
      "A borrower's guide to reading business loan offers: comparing APR to factor rate, hidden fees, prepayment clauses, and the questions to ask before you sign.",
    heroImage:
      "https://images.pexels.com/photos/210574/pexels-photo-210574.jpeg?auto=compress&cs=tinysrgb&w=1600",
  },
};

export const getCategorySeo = (slug, name) => {
  return (
    CATEGORY_SEO[slug] || {
      heading: name,
      tagline: `Field notes on ${name.toLowerCase()} — plain-English guides from working brokers.`,
      seoTitle: `${name} — The Steady Path Journal`,
      seoDescription: `Articles about ${name.toLowerCase()} — practical, honest, jargon-free guides from a commercial finance broker.`,
      heroImage: null,
    }
  );
};
